/**
 * Rubric Detail View Component
 * 
 * Displays detailed criterion-level feedback and scores for coaching and improvement.
 * Implements FRRP04 requirements for detailed rubric analysis.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    Alert,
    Skeleton,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    SelectChangeEvent
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ArrowBack as ArrowBackIcon,
    Assessment as AssessmentIcon,
    Feedback as FeedbackIcon,
    TipsAndUpdates as TipsIcon,
    PlayArrow as PlayIcon,
    Person as PersonIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseClient';

import { useUser } from '../../contexts/UserContext';
import { formatScore, getScoreColor, getScoreGrade } from '../../utils/api/reportingApi';

// Interface definitions
interface TranscriptExcerpt {
    speaker: string;
    text: string;
    startTime: number;
    confidence?: number;
}

interface Criterion {
    criterionName: string;
    score: number;
    feedback: string;
    recommendations?: string[];
    transcriptExcerpts?: TranscriptExcerpt[];
    [key: string]: any;
}

interface RubricCategory {
    criteria: Criterion[];
    [key: string]: any;
}

interface RubricDetails {
    rubricDetails: Record<string, RubricCategory>;
    totalCriteria: number;
    [key: string]: any;
}

const CriterionCard = ({ criterion, category }: { criterion: Criterion, category: string }) => {
    const { criterionName, score, feedback, recommendations, transcriptExcerpts } = criterion;

    const scoreColor = getScoreColor(score);
    const grade = getScoreGrade(score);

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                            {criterionName}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', ml: 2 }}>
                        <Typography variant="h4" color={scoreColor === 'default' ? 'textPrimary' : (scoreColor as any)} gutterBottom>
                            {formatScore(score)}
                        </Typography>
                        <Chip
                            label={`Grade: ${grade}`}
                            color={scoreColor === 'default' ? undefined : (scoreColor as any)}
                            size="small"
                        />
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Feedback Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FeedbackIcon color="primary" />
                        Detailed Feedback
                    </Typography>
                    <Typography variant="body2" sx={{ pl: 4 }}>
                        {feedback || 'No specific feedback available.'}
                    </Typography>
                </Box>

                {/* Recommendations Section */}
                {recommendations && recommendations.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TipsIcon color="secondary" />
                            Recommendations for Improvement
                        </Typography>
                        <List dense sx={{ pl: 2 }}>
                            {recommendations.map((recommendation, index) => (
                                <ListItem key={index} sx={{ py: 0.5, pl: 2 }}>
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: 'secondary.main'
                                            }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText>
                                        <Typography variant="body2">
                                            {recommendation}
                                        </Typography>
                                    </ListItemText>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Video Analysis Excerpts Section */}
                {transcriptExcerpts && transcriptExcerpts.length > 0 && (
                    <Box>
                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlayIcon color="info" />
                            Relevant Video Analysis Excerpts
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                            {transcriptExcerpts.map((excerpt, index) => (
                                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Avatar sx={{ width: 24, height: 24, bgcolor: excerpt.speaker === 'Presenter' ? 'primary.main' : 'secondary.main' }}>
                                            {excerpt.speaker === 'Presenter' ? <PersonIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
                                        </Avatar>
                                        <Typography variant="caption" fontWeight="bold">
                                            {excerpt.speaker}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            ({Math.floor(excerpt.startTime / 60)}:{String(Math.floor(excerpt.startTime % 60)).padStart(2, '0')})
                                        </Typography>
                                        {excerpt.confidence && (
                                            <Chip
                                                label={`${Math.round(excerpt.confidence * 100)}% confidence`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                        "{excerpt.text}"
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const CategorySummary = ({ category, criteria }: { category: string, criteria: Criterion[] }) => {
    if (!criteria || criteria.length === 0) return null;

    const averageScore = criteria.reduce((sum, c) => sum + (c.score || 0), 0) / criteria.length;
    const scoreColor = getScoreColor(averageScore);

    return (
        <Card sx={{ mb: 2, bgcolor: `${scoreColor}.50` }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            {category}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {criteria.length} criteria evaluated
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h3" color={scoreColor === 'default' ? 'textPrimary' : (scoreColor as any)}>
                            {formatScore(averageScore)}
                        </Typography>
                        <Chip
                            label={`Grade: ${getScoreGrade(averageScore)}`}
                            color={scoreColor === 'default' ? undefined : (scoreColor as any)}
                        />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const RubricDetailView: React.FC = () => {
    const { presentationId } = useParams<{ presentationId: string }>();
    const navigate = useNavigate();
    const { user, userProfile } = useUser();

    const [rubricData, setRubricData] = useState<RubricDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState('all');

    useEffect(() => {
        if (presentationId) {
            loadRubricDetails();
        }
    }, [presentationId, selectedCategory]);

    const loadRubricDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            const category = selectedCategory === 'all' ? null : selectedCategory;

            if (!presentationId) {
                setError('Missing presentation ID');
                return;
            }

            const presRef = doc(db, 'presentations', presentationId);
            const presSnap = await getDoc(presRef);
            if (!presSnap.exists()) {
                setError('Presentation not found');
                return;
            }

            const pres = presSnap.data() as any;
            if (userProfile?.tenantId && pres.tenantId && pres.tenantId !== userProfile.tenantId) {
                setError('Access denied');
                return;
            }

            const finalReport = pres.finalReport;
            const detailed = finalReport?.detailed_evaluations;
            if (!finalReport || !detailed || typeof detailed !== 'object') {
                setError('No rubric details available for this presentation.');
                return;
            }

            const rubricDetails: Record<string, RubricCategory> = {};

            const formatCategoryName = (key: string) =>
                key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

            Object.entries(detailed as Record<string, any>).forEach(([key, val]) => {
                const displayCategory = formatCategoryName(key);
                if (category && displayCategory !== category) return;

                const criteriaScores: any[] = Array.isArray((val as any)?.criteria_scores) ? (val as any).criteria_scores : [];
                rubricDetails[displayCategory] = {
                    criteria: criteriaScores.map((c: any) => ({
                        criterionName: c?.criterion_name || 'Unnamed criterion',
                        score: typeof c?.score === 'number' ? c.score : 0,
                        feedback: c?.feedback || '',
                        recommendations: Array.isArray(c?.recommendations) ? c.recommendations : [],
                        transcriptExcerpts: Array.isArray(c?.transcript_excerpts) ? c.transcript_excerpts : undefined
                    }))
                };
            });

            const allCriteria = Object.values(rubricDetails).flatMap(cat => cat.criteria || []);

            setRubricData({
                rubricDetails,
                totalCriteria: allCriteria.length
            });
        } catch (err: any) {
            setError(err.message || 'Failed to load rubric details');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToReport = () => {
        navigate(`/presentations/${presentationId}/report`);
    };

    const handleCategoryChange = (event: SelectChangeEvent) => {
        setSelectedCategory(event.target.value);
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Skeleton variant="text" width="60%" height={60} />
                <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rectangular" width="100%" height={150} sx={{ mt: 2 }} />
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button onClick={loadRubricDetails} startIcon={<AssessmentIcon />}>
                    Retry
                </Button>
            </Box>
        );
    }

    if (!rubricData || !rubricData.rubricDetails) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">
                    No rubric details available for this presentation.
                </Alert>
            </Box>
        );
    }

    const categories = Object.keys(rubricData.rubricDetails);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={handleBackToReport} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Detailed Rubric Analysis
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            Criterion-level feedback and coaching recommendations
                        </Typography>
                    </Box>
                </Box>

                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Category</InputLabel>
                    <Select
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        label="Filter by Category"
                    >
                        <MenuItem value="all">All Categories</MenuItem>
                        {categories.map(category => (
                            <MenuItem key={category} value={category}>
                                {category}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Summary Statistics */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={3} sx={{ textAlign: 'center' }}>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Typography variant="h4" color="primary">
                                {rubricData.totalCriteria}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Total Criteria
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Typography variant="h4" color="secondary">
                                {categories.length}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Categories
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Typography variant="h4" color="info">
                                {Object.values(rubricData.rubricDetails)
                                    .flatMap(cat => cat.criteria)
                                    .filter(criterion => criterion.transcriptExcerpts && criterion.transcriptExcerpts.length > 0)
                                    .length
                                }
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                With Video Analysis Examples
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Typography variant="h4" color="success">
                                {Object.values(rubricData.rubricDetails)
                                    .flatMap(cat => cat.criteria)
                                    .filter(criterion => criterion.recommendations && criterion.recommendations.length > 0)
                                    .length
                                }
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                With Recommendations
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Category Details */}
            {selectedCategory === 'all' ? (
                // Show all categories
                categories.map(category => (
                    <Box key={category} sx={{ mb: 4 }}>
                        <CategorySummary
                            category={category}
                            criteria={rubricData.rubricDetails[category]?.criteria || []}
                        />

                        {rubricData.rubricDetails[category]?.criteria?.map((criterion, index) => (
                            <CriterionCard
                                key={`${category}-${index}`}
                                criterion={criterion}
                                category={category}
                            />
                        ))}
                    </Box>
                ))
            ) : (
                // Show selected category only
                <Box>
                    <CategorySummary
                        category={selectedCategory}
                        criteria={rubricData.rubricDetails[selectedCategory]?.criteria || []}
                    />

                    {rubricData.rubricDetails[selectedCategory]?.criteria?.map((criterion, index) => (
                        <CriterionCard
                            key={`${selectedCategory}-${index}`}
                            criterion={criterion}
                            category={selectedCategory}
                        />
                    ))}
                </Box>
            )}

            {/* Helper Text */}
            <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                    <strong>How to use this view:</strong> This detailed rubric analysis shows individual criterion scores,
                    AI-generated feedback, and actionable recommendations. Video analysis excerpts provide context for
                    the evaluation. Use this information for coaching sessions and targeted skill improvement.
                </Typography>
            </Alert>
        </Box>
    );
};

export default RubricDetailView;
